import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import { OSM } from 'ol/source';
import { DragRotateAndZoom, defaults as defaultInteractions } from 'ol/interaction';
import * as olProj from 'ol/proj';
import * as moment from 'moment-jalaali';
import * as XLSX from 'xlsx';
import axios from 'src/app/Service/axios';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { MapService } from 'src/app/Service/map.service';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';

declare const $: any;

@Component({
  selector: 'manageEvent',
  templateUrl: './manageEventComponent.html',
})
export class ManageEventComponent implements AfterViewInit, OnDestroy {
  @ViewChild('timePicker') timePicker!: ElementRef<HTMLDivElement>;
  @ViewChild('mapTarget') mapTarget!: ElementRef<HTMLDivElement>;
  @ViewChild('mapModal') mapModal!: ElementRef<HTMLDivElement>;

  domain: any[] = [];
  journalTypeData: any[] = [];
  EventsData: any[] = [];
  fullEventsData: any[] = [];
  pagedRows: any[] = [];
  readonly bDailyEvent = this.route.snapshot.url[0]?.path === 'daily-event';
  loading = false;
  hasError = false;
  hasSearched = false;
  filtersOpen = true;
  searchText = '';
  currentPage = 1;
  pageSize = 20;
  dateSort: 'asc' | 'desc' | null = null;
  errorMessage = '';
  private map?: Map;
  private refreshTimer?: ReturnType<typeof setInterval>;
  private destroyed = false;

  eventModel = this.formBuilder.group({
    iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
    strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    iMobileDomain: '-1',
    tiJournalType: '-1',
    strDateTime: '',
    strDate: moment().format('jYYYY/jMM/jDD'),
    strTime: moment().format('HH:mm'),
  });

  constructor(
    private notifyService: NotificationService,
    private otherService: OtherService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private mapService: MapService,
    private gurdService: GurdServiceService,
  ) {}

  get activeFilterCount(): number {
    return ['iMobileDomain', 'tiJournalType'].filter(key => this.eventModel.get(key)?.value != -1).length;
  }

  get hasActiveFilters(): boolean {
    return !!this.searchText.trim() || this.activeFilterCount > 0;
  }

  ngAfterViewInit(): void {
    $(this.timePicker.nativeElement).clockpicker({ donetext: 'top', align: 'top', autoclose: true });
    $(this.mapModal.nativeElement).on('shown.bs.modal.manageEvent', () => this.map?.updateSize());
    void this.loadOptions('GetMobileDomain4Report?iSystemUser=' + this.eventModel.value.iSystemUser, {}, 'domain');
    void this.loadOptions('GetJournalType', {
      iSystemUser: this.eventModel.value.iSystemUser,
      strSession: this.eventModel.value.strSession,
    }, 'journalTypeData');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearInterval(this.refreshTimer);
    $(this.timePicker.nativeElement).clockpicker('remove');
    $(this.mapModal.nativeElement).off('.manageEvent').modal('hide').modal('dispose');
    $('#eventMapTooltip').tooltip('dispose');
    this.map?.setTarget(undefined);
    this.map?.dispose();
  }

  private async loadOptions(endpoint: string, model: object, key: 'domain' | 'journalTypeData'): Promise<void> {
    try {
      const response = await axios.post(endpoint, model);
      if (this.destroyed) return;
      const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (!Array.isArray(data)) throw new Error(data?.strError);
      this[key] = data;
    } catch {
      if (!this.destroyed) this.notifyService.showError('دریافت گزینه‌های فیلتر ناموفق بود؛ صفحه را دوباره بارگذاری کنید.');
    }
  }

  async search(): Promise<void> {
    if (this.loading || this.destroyed) return;
    const time = $(this.timePicker.nativeElement).find('input').val();
    this.eventModel.patchValue({ strTime: time });
    if (!this.eventModel.value.strDate || (!this.bDailyEvent && !/^([01]\d|2[0-3]):[0-5]\d$/.test(time))) {
      this.filtersOpen = true;
      this.notifyService.showError('تاریخ و ساعت معتبر انتخاب کنید.');
      return;
    }
    clearInterval(this.refreshTimer);
    this.eventModel.patchValue({ strDateTime: '' });
    this.fullEventsData = [];
    this.refreshTable(true);
    this.hasSearched = true;
    this.filtersOpen = false;
    await this.getEventsData();
    if (!this.bDailyEvent && !this.destroyed) {
      this.refreshTimer = setInterval(() => void this.getEventsData(), 30000);
    }
  }

  async getEventsData(): Promise<void> {
    if (this.loading || this.destroyed) return;
    this.loading = true;
    this.hasError = false;
    try {
      const model = this.otherService.stringNumberToNumber(this.eventModel.value);
      const response = await axios.post(this.bDailyEvent ? 'Journal_DailyReport' : 'GetLastJournal', model);
      if (this.destroyed) return;
      const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (!Array.isArray(data)) throw new Error(data?.strError || 'پاسخ دریافتی از سرور معتبر نیست.');
      let cursor = data[0]?.strCurrentDateTime;
      if (!data.length && !this.bDailyEvent) {
        const current = await axios.post('GetCurrentDateTime', {});
        if (this.destroyed) return;
        cursor = current.data;
      }
      this.fullEventsData = this.bDailyEvent ? data : [...this.fullEventsData, ...data];
      if (cursor) this.eventModel.patchValue({ strDateTime: cursor });
      this.refreshTable();
    } catch (error) {
      if (!this.destroyed) {
        this.hasError = true;
        this.errorMessage = error instanceof Error ? error.message : 'دریافت اطلاعات با خطا مواجه شد.';
      }
    } finally {
      if (!this.destroyed) this.loading = false;
    }
  }

  refreshTable(resetPage = false): void {
    const query = this.searchText.trim().toLocaleLowerCase();
    const { iMobileDomain, tiJournalType } = this.eventModel.value;
    this.EventsData = this.fullEventsData.filter(row =>
      (iMobileDomain == -1 || row.iMobileDomain == iMobileDomain) &&
      (tiJournalType == -1 || row.tiJournalType == tiJournalType) &&
      (!query || ['strFullName', 'strMobile', 'strVehicleNo', 'strMobileDomain_strComment',
        'strJournalType_strComment', 'strComment', 'strDate', 'strTime', 'strMobileType_strComment',
        'strMobileGroup_strComment'].some(key => String(row[key] ?? '').toLocaleLowerCase().includes(query))));
    if (this.dateSort) {
      this.EventsData.sort((a, b) => `${a.strDate ?? ''} ${a.strTime ?? ''}`.localeCompare(
        `${b.strDate ?? ''} ${b.strTime ?? ''}`) * (this.dateSort === 'asc' ? 1 : -1));
    }
    if (resetPage) this.currentPage = 1;
    this.updatePage();
  }

  clearFilters(): void {
    if (this.loading) return;
    this.searchText = '';
    this.eventModel.patchValue({ iMobileDomain: '-1', tiJournalType: '-1', strDate: moment().format('jYYYY/jMM/jDD') });
    this.refreshTable(true);
  }

  sortByDate(): void {
    this.dateSort = this.dateSort === 'asc' ? 'desc' : 'asc';
    this.refreshTable(true);
  }

  private updatePage(): void {
    this.currentPage = Math.min(this.currentPage, Math.max(1, Math.ceil(this.EventsData.length / this.pageSize)));
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.EventsData.slice(start, start + this.pageSize);
  }

  changePage(page: number): void {
    this.currentPage = page;
    this.updatePage();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.updatePage();
  }

  exportexcel(): void {
    const fields = this.bDailyEvent
      ? ['strMobileType_strComment', 'strFullName', 'strMobile', 'strVehicleNo', 'strMobileDomain_strComment', 'strJournalType_strComment', 'iCount']
      : ['strDate', 'strTime', 'strJournalType_strComment', 'strFullName', 'strMobile', 'strVehicleNo', 'strMobileDomain_strComment', 'strMobileGroup_strComment', 'strComment', 'fLat', 'fLon', 'strMobileType_strComment', 'strMobileGroup_strComment', 'tiMaxSpeed'];
    const headers = this.bDailyEvent
      ? ['نوع خودرو', 'نام راننده', 'موبایل راننده', 'پلاک', 'حوزه خدمت', 'تخلف', 'تعداد']
      : ['تاریخ', 'ساعت', 'نوع واقعه', 'نام راننده', 'موبایل راننده', 'پلاک', 'حوزه خدمت', 'نوع خودرو', 'توضیحات', 'عرض جغرافیایی', 'طول جغرافیایی', 'گروه خودرو', 'کاربری', 'سرعت مجاز'];
    const rows = this.EventsData.map((row, index) => [index + 1, ...fields.map(key => row[key] ?? '')]);
    const sheet = XLSX.utils.aoa_to_sheet([['ردیف', ...headers], ...rows]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Sheet1');
    XLSX.writeFile(book, this.bDailyEvent ? 'dailyEvent.xlsx' : 'LiveJournal.xlsx');
  }

  openMap(row: any): void {
    if (this.loading) return;
    if (!this.map) {
      this.map = new Map({
        interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
        target: this.mapTarget.nativeElement,
        layers: [new TileLayer({ source: new OSM() })],
        view: new View({ center: olProj.fromLonLat([51.3833554, 35.6770113]), zoom: 5 }),
      });
      this.map.on('click', event => {
        if (!event.dragging) this.showData(this.mapService.mapPointerMove(event.pixel, this.map));
      });
      this.map.getView().on('propertychange', () => $('#eventMapTooltip').tooltip('hide'));
    }
    $('#eventMapTooltip').tooltip('hide');
    this.mapService.removeMarkerById('marker', this.map);
    const comment = String(row.strComment ?? '').replace(/[&<>"']/g, character =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]!));
    this.map.addLayer(this.mapService.createMarker([row.fLon, row.fLat], '../../assets/Img/marker5.png', 'marker', '', {
      html: `<p class="text-dark mb-0 f12">${comment}</p>`,
    }));
    this.map.getView().setCenter(olProj.fromLonLat([row.fLon, row.fLat]));
    this.map.getView().setZoom(17);
    $(this.mapModal.nativeElement).modal('show');
  }

  showData(item: any): void {
    const tooltip = $('#eventMapTooltip');
    const data = item.featureLayer?.feature?.get('data');
    if (!data?.html) return tooltip.tooltip('hide');
    tooltip.tooltip({ html: true, animation: true, trigger: 'manual', placement: 'top' });
    tooltip.css({ left: item.pixel[0] + 'px', top: item.pixel[1] - 14 + 'px', zIndex: 1000 });
    tooltip.attr('data-bs-original-title', data.html).tooltip('show');
  }
}
